import Glide, {
  Breakpoints,
  Controls,
  Swipe,
  Images,
  Anchors,
} from "@glidejs/glide/dist/glide.modular.esm";
import { createProductImpressionObserver } from "./analytics";
import { i18n } from "./i18n";
import { Logger, shuffleArray } from "@fruugo/utilities";
import { CarouselResponse, DeviceWidth, ProductTile } from "../types";
import { ReactElement } from "jsx-dom";
import { isDeviceWidthBetween } from "./utilities";
import { OtherSellersChip } from "./components/OtherSellersChip";
import { initSelectSellerMenu } from "./components/side-menu/SelectSellerMenu";
import { getCarouselData } from "./api";

const Log = new Logger(window.environment);
const observer = createProductImpressionObserver();

export const initCarousels = (
  targets = document.getElementsByClassName("js-carousel")
) => {
  if (targets) {
    Array.from(targets).forEach((target) => {
      const element = target as HTMLElement;
      if (typeof element.dataset !== "undefined") {
        initCarousel(element)
          .then((carousel) => {
            const endpoint = element.dataset.endpoint;
            if (!endpoint) return;

            return getCarouselData(endpoint)
              .then((data) => populateCarousel(element, carousel, data))
              .then(initSelectSellerMenu);
          })
          .catch((error) => {
            element.remove();
            Log.error(error);
          });
      }
    });
  }
};

const initCarousel = (target: HTMLElement) => {
  return new Promise<Glide>((resolve) => {
    const carousel = new Glide(target, {
      type: "slider",
      perView: 8,
      gap: 16,
      perSwipe: "|",
      direction: window.rtl ? "rtl" : "ltr",
      animationDuration: 800,
      peek: 16,
      breakpoints: {
        576: {
          perView: 2,
          peek: {
            before: 16,
            after: 50,
          },
          animationDuration: 200,
        },
        768: {
          perView: 3,
          peek: {
            before: 16,
            after: 50,
          },
          animationDuration: 300,
        },
        1280: {
          perView: 5,
          animationDuration: 800,
        },
        1600: {
          perView: 6,
        },
      },
    });
    resolve(carousel);
  });
};

export const showCarouselArrows: (target: HTMLElement) => void = (
  target: HTMLElement
) =>
  target
    .querySelectorAll(".glide__arrow")
    .forEach((arrow) => arrow.classList.add("d-md-block"));

export const hideCarouselArrows: (target: HTMLElement) => void = (
  target: HTMLElement
) =>
  target
    .querySelectorAll(".glide__arrow")
    .forEach((arrow) => arrow.classList.remove("d-md-block"));

const createProductTileMarkup = (data: ProductTile) => {
  const {
    productId,
    title,
    originalTitle,
    englishCategories,
    numericTotalDiscount,
    numericTotalForAnalytics,
    eurNumericTotalForAnalytics,
    brand,
  } = data;

  const tile = (
    <li
      class="glide__slide"
      data-item-parent
      data-product-id={productId}
      data-name={originalTitle}
      data-category={
        englishCategories && englishCategories.length > 0
          ? englishCategories[0]
          : null
      }
      data-price={numericTotalForAnalytics}
      data-discount={numericTotalDiscount}
      data-eur-price={eurNumericTotalForAnalytics}
      data-brand={brand}
    >
      <div className="carousel-item">
        <a
          href={data.productPageLink.href}
          class="tw:p-2 tw:block tw:hover:no-underline"
          aria-label={i18n("product.go-to-page", data.title)}
        >
          <span class="carousel-item-image">
            <img
              src={
                data.imageUrl
                  ? data.imageUrl
                  : "/marketplace/images/image-placeholder.png"
              }
              alt={title}
              width="120"
              height="120"
              loading="lazy"
              onError={(event) => handleImageError(event.target)}
              aria-hidden="true"
              data-item-image
            />
          </span>
          <h5
            class="carousel-item-title"
            aria-label={i18n("product.go-to-page", title)}
            data-item-title
          >
            {data.title}
          </h5>
          <div class="tw:flex tw:flex-row tw:gap-2 tw:mt-4 tw:items-center">
            <span class="tw:text-fruugo-low-key tw:lowercase tw:font-normal">
              {i18n("search.page.from")}
            </span>
            <span class="tw:text-fruugo-green tw:font-bold tw:text-xl">
              {data.price}
            </span>
          </div>
        </a>
        {data.otherSellersLinkEnabled && (
          <OtherSellersChip productTile={data} />
        )}
      </div>
    </li>
  );

  return tile;
};

const populateCarousel = (
  target: HTMLElement,
  carousel: Glide,
  data: CarouselResponse
) => {
  const { productTiles, title } = data;
  const carouselWrapper = target.querySelector(".glide__slides") as
    | HTMLElement
    | undefined;
  const minTiles = 4;
  const { shuffle } = target.dataset;

  if (shuffle !== "false") shuffleArray(productTiles);

  if (carouselWrapper) {
    populateCarouselTitle(target, title);
    populateTiles(
      productTiles,
      target,
      carouselWrapper,
      createProductTileMarkup
    );
  }

  if (productTiles && productTiles.length < minTiles) {
    carousel.settings.type = "slider";
  } else {
    handleCarouselArrows(productTiles.length, target);
  }

  carousel.mount({ Breakpoints, Controls, Swipe, Images, Anchors });
};

function handleCarouselArrows(productCount: number, target: HTMLElement) {
  const updateArrows = () => {
    const shouldShow =
      (isDeviceWidthBetween(DeviceWidth.Medium, DeviceWidth.XLarge) &&
        productCount > 5) ||
      (isDeviceWidthBetween(DeviceWidth.XLarge, DeviceWidth.XXLarge) &&
        productCount > 6) ||
      (isDeviceWidthBetween(DeviceWidth.XXLarge) && productCount > 8);

    if (shouldShow) {
      showCarouselArrows(target);
    } else {
      hideCarouselArrows(target);
    }
  };

  updateArrows();
  window.addEventListener("resize", updateArrows);
}

const populateCarouselTitle = (target: HTMLElement, title: string) => {
  const placeholder = target.querySelector(".loading");
  if (placeholder) placeholder.remove();

  if (!title) return;

  const carouselTitle = document.createElement("h4");
  carouselTitle.innerHTML = title;
  carouselTitle.classList.add("carousel-heading");

  target.insertBefore(carouselTitle, target.firstChild);
};

const populateTiles = (
  tiles: ProductTile[],
  target: HTMLElement,
  carouselWrapper: HTMLElement,
  createMarkup: (tile: ProductTile) => ReactElement
) => {
  if (tiles && tiles.length > 0) {
    carouselWrapper.innerHTML = "";
    tiles.forEach((tile) => {
      const markup = createMarkup(tile);
      carouselWrapper.appendChild(markup);
      observer.observe(markup);
    });
  } else {
    target.remove();
  }
};
